import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

import { ROUTE } from "@/route/app_routes.js";
import { verifyEmail, resendVerificationCode } from "@/services/authService";
import { getErrorMessage } from "@/services/apiClient";

const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 60;

const VerifyEmailForm = () => {
  const { t } = useTranslation("dashboard");
  const router = useRouter();
  const { email: queryEmail, code: queryCode } = router.query;

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [serverError, setServerError] = useState(null);
  const [verified, setVerified] = useState(false);

  const inputRefs = useRef([]);
  const email = typeof queryEmail === "string" ? queryEmail : "";

  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const submitCode = async (code) => {
    if (!email) {
      setServerError(
        t(
          "authWrapper.verifyEmail.form.missingEmailError",
          "We couldn't tell which account to verify. Please sign up again or use the link from your email."
        )
      );
      return;
    }
    setServerError(null);
    setSubmitting(true);
    try {
      await verifyEmail({ email, code });
      setVerified(true);
    } catch (error) {
      const message = getErrorMessage(error);
      setServerError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  // Deep-link from the activation email (?email=...&code=123456) verifies automatically.
  useEffect(() => {
    if (typeof queryCode === "string" && queryCode.length === CODE_LENGTH && email) {
      setDigits(queryCode.split(""));
      submitCode(queryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryCode, email]);

  const focusBox = (index) => {
    inputRefs.current[index]?.focus();
    inputRefs.current[index]?.select();
  };

  const handleChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);

    if (digit && index < CODE_LENGTH - 1) {
      focusBox(index + 1);
    }

    if (next.every((d) => d) && next.join("").length === CODE_LENGTH) {
      submitCode(next.join(""));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      focusBox(index - 1);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill("");
    pasted.split("").forEach((d, i) => { next[i] = d; });
    setDigits(next);
    focusBox(Math.min(pasted.length, CODE_LENGTH - 1));
    if (pasted.length === CODE_LENGTH) {
      submitCode(pasted);
    }
  };

  const handleResend = async () => {
    if (!email || cooldown > 0) return;
    setResending(true);
    try {
      const { message } = await resendVerificationCode({ email });
      toast.success(
        message || t("authWrapper.verifyEmail.form.defaultResendSuccess", "A new code is on its way to your inbox.")
      );
      setCooldown(RESEND_COOLDOWN);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className="rbt-auth-split__form-card">
        <div className="rbt-auth-split__success">
          <span className="rbt-auth-split__success-icon">
            <i className="feather-check"></i>
          </span>
          <h1 className="title">{t("authWrapper.verifyEmail.form.verifiedTitle", "Email verified")}</h1>
          <p>{t("authWrapper.verifyEmail.form.verifiedBody", "Your account is active. You're ready to start learning — for free.")}</p>
          <Link className="rbt-btn btn-md btn-gradient hover-icon-reverse w-100" href={ROUTE.dashboard}>
            <span className="icon-reverse-wrapper">
              <span className="btn-text">{t("authWrapper.verifyEmail.form.goToDashboard", "Go to my dashboard")}</span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
            </span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rbt-auth-split__form-card">
      <div className="rbt-auth-split__welcome">
        <h1 className="title">{t("authWrapper.verifyEmail.form.checkEmailTitle", "Check your email")}</h1>
        <p>
          {email ? (
            <>
              {t("authWrapper.verifyEmail.form.sentCodeToEmailPrefix", "We sent a 6-digit code to")} <strong>{email}</strong>.{" "}
              {t("authWrapper.verifyEmail.form.sentCodeToEmailSuffix", "Enter it below to activate your account.")}
            </>
          ) : (
            t("authWrapper.verifyEmail.form.enterCodeGeneric", "Enter the 6-digit verification code from your email.")
          )}
        </p>
      </div>

      {serverError && (
        <div className="rbt-auth-split__alert" role="alert">
          <i className="feather-alert-circle"></i>
          <span>{serverError}</span>
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submitCode(digits.join(""));
        }}
        noValidate
      >
        <div className="rbt-auth-split__code-row" onPaste={handlePaste}>
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              className="rbt-auth-split__code-box"
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              disabled={submitting}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              aria-label={t("authWrapper.verifyEmail.form.digitAriaLabel", "Digit {{index}} of {{total}}", { index: index + 1, total: CODE_LENGTH })}
            />
          ))}
        </div>

        <div className="form-submit-group">
          <button
            type="submit"
            className="rbt-btn btn-md btn-gradient hover-icon-reverse w-100"
            disabled={submitting || digits.some((d) => !d)}
          >
            <span className="icon-reverse-wrapper">
              <span className="btn-text">
                {submitting && <span className="rbt-auth-split__spinner" aria-hidden="true" />}
                {submitting
                  ? t("authWrapper.verifyEmail.form.verifying", "Verifying…")
                  : t("authWrapper.verifyEmail.form.verifyEmail", "Verify email")}
              </span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
              <span className="btn-icon"><i className="feather-arrow-right"></i></span>
            </span>
          </button>
        </div>
      </form>

      <div className="rbt-auth-split__resend">
        {t("authWrapper.verifyEmail.form.resendPrompt", "Didn't get a code?")}{" "}
        <button type="button" onClick={handleResend} disabled={resending || cooldown > 0}>
          {cooldown > 0
            ? t("authWrapper.verifyEmail.form.resendIn", "Resend in {{seconds}}s", { seconds: cooldown })
            : resending
            ? t("authWrapper.verifyEmail.form.sending", "Sending…")
            : t("authWrapper.verifyEmail.form.resendCode", "Resend code")}
        </button>
      </div>
    </div>
  );
};

export default VerifyEmailForm;
